import { Table, TableColumn } from '@wix/design-system';
import React, {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getArticlesByCategoryId } from '../../common/api/getArticlesByCategoryId';
import { CategoryItemType } from '../../common/api/getCategories';
import { SearchArticlesItem } from '../../common/api/searchArticles';
import { Loader } from '../../common/components/Loader';
import { usePageEngagement } from '../../common/hooks/usePageEngagement';
import { ArticlesListItem } from '../../help-center/components/ArticlesListItem';
import { biHelpCenter, PresentedState } from '../../util/bi';

const pageSize = 30;
let page = 0;

type Props = {
  category: CategoryItemType;
};

export const dataHook = 'category-articles';

export const CategoryArticles: FC<Props> = ({ category }) => {
  const { id: categoryId } = category;
  const containerRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<RefObject<HTMLDivElement> | null>(
    null,
  );
  const [articles, setArticles] = useState<SearchArticlesItem[] | undefined>(
    undefined,
  );
  const articlesRef = useRef<SearchArticlesItem[] | undefined>(articles);
  const [hasMore, setHasMore] = useState(true);
  const { onScroll } = usePageEngagement();

  const columns: TableColumn<SearchArticlesItem>[] = React.useMemo(
    () => [
      {
        title: '', // because of titleBarVisible={false}
        style: { padding: 0, boxShadow: 'none' },
        render: (article, i) => (
          <ArticlesListItem
            id={article.id}
            interactionLogger={biHelpCenter.logCategorySolutionListInteraction.bind(
              biHelpCenter,
            )}
            rowNumber={i}
            title={article.title}
            categoryId={categoryId}
          />
        ),
      },
    ],
    [categoryId],
  );

  const fetchArticles = useCallback(async () => {
    const newArticles = await getArticlesByCategoryId(
      categoryId,
      pageSize,
      page++,
    );

    if (newArticles.length < pageSize) {
      setHasMore(false);
    }

    if (!category.children?.length) {
      biHelpCenter.categoryArticlesList = newArticles;
      biHelpCenter.replaceCurrentPresentedState(PresentedState.SolutionList);
      biHelpCenter.logCategorySolutionListPresented(newArticles);
    }

    setArticles((previousArticles) => {
      return (articlesRef.current = previousArticles
        ? [...previousArticles, ...newArticles]
        : newArticles);
    });
  }, [category.children?.length, categoryId]);

  const resetArticles = useCallback(() => {
    page = 0;
    setHasMore(true);
    setArticles(undefined);
    articlesRef.current = undefined;
  }, []);

  useEffect(() => {
    setContainer(containerRef);
  }, []);

  useEffect(() => {
    resetArticles();
    void fetchArticles();
  }, [categoryId]);

  return (
    <div
      ref={containerRef}
      style={{ height: '100%', maxHeight: '100%', overflowY: 'scroll' }}
      data-hook={dataHook}
      onScroll={onScroll}
    >
      {articles ? (
        <Table
          infiniteScroll
          hasMore={hasMore}
          loadMore={fetchArticles}
          itemsPerPage={pageSize}
          data={articles}
          columns={columns}
          scrollElement={container! && container.current!}
          loader={<Loader dataHook="category-articles__loader" margin="25px" />}
        >
          <Table.Content titleBarVisible={false} />
        </Table>
      ) : (
        <Loader
          dataHook="category-articles__loader"
          height="100%"
          verticalAlign="middle"
        />
      )}
    </div>
  );
};
